import {z} from 'zod';

import {RoleFirebaseStringSchema} from '../firebase/documents/role';

// ============================================================================
// Roadmap Item Types
// ============================================================================

export const ALL_ROADMAP_ITEM_TYPES = [
  'Assignment',
  'Math Assignment',
  'Lesson',
  'Self Check',
  'Link',
  'Create Assignment',
  'Section',
  'Section Shadow',
  'Section Debrief',
  'Section Feedback',
  'auto-check',
  'One of',
] as const;

export type RoadmapItemType = (typeof ALL_ROADMAP_ITEM_TYPES)[number];

export const RoadmapItemTypeSchema = z.enum(ALL_ROADMAP_ITEM_TYPES);

// ============================================================================
// RoadmapItem Schema
// ============================================================================

/** Schema for an individual item within a roadmap module. */
export const RoadmapItemSchema = z
  .object({
    id: z.string(),
    itemType: RoadmapItemTypeSchema,
    title: z.string(),
    published: z.boolean(),
    startTiedToSection: z.boolean(),
    endTiedToSection: z.boolean(),
    isOptional: z.boolean(),
    completionId: z.string().optional(),
    urlId: z.string().optional(),
    lessonNum: z.number().optional(),
    /**
     * Optional icon override for this item, a key from the webapp's closed
     * icon set (`SUPPORTED_TODO_ICON_OVERRIDES`). Omitted → the course's
     * default for the item type, then the built-in icon.
     */
    icon: z.string().optional(),
    /**
     * Optional link override. When set and non-blank, students are sent
     * here instead of the link derived from the item's type and ids.
     */
    href: z.string().optional(),
    /**
     * Optional one-line description. The PAI maps show it in a link item's
     * callout and as the item's tooltip. When omitted, neither shows one.
     */
    description: z.string().optional(),
    /**
     * For a `One of` item: the Math IDE assignment ids the student picks
     * from. The item is complete once any one of them is. Other item types
     * omit it.
     */
    choices: z.array(z.string()).optional(),
  })
  .loose();

export type RoadmapItem = z.infer<typeof RoadmapItemSchema>;

// ============================================================================
// RoadmapModule Schema
// ============================================================================

/** Schema for a roadmap module (a group of items). */
export const RoadmapModuleSchema = z
  .object({
    type: z.string(),
    selfCheck: z.boolean(),
    completionPath: z.string().optional(),
    title: z.string(),
    id: z.string(),
    isStudentModule: z.boolean(),
    roadmapType: RoleFirebaseStringSchema,
    startDate: z.string(), // labeled as release date in the UI
    items: z.array(RoadmapItemSchema),
    thumbnailUrl: z.string().optional(),
    // Whether students see the module on the roadmap, locked, before its
    // release: `shown` always, `hidden` never, `auto` (or absent) while it is
    // the next to release. Once released it always shows.
    roadmapVisibility: z.enum(['auto', 'shown', 'hidden']).optional(),
  })
  .loose();

export type RoadmapModule = z.infer<typeof RoadmapModuleSchema>;

// ============================================================================
// Extracted Data Types
// ============================================================================

/** Base interface shared by all roadmap item data types. */
export interface RoadmapItemData {
  /** The parent roadmap module that contains this item. */
  module: {title: string; id: string};
  title: string;
}

/** Data for an assignment item extracted from the roadmap. */
export interface RoadmapAssignmentData extends RoadmapItemData {
  assnId: string;
}

/** Data for a lesson item extracted from the roadmap. */
export interface RoadmapLessonData extends RoadmapItemData {
  lessonId: string;
  lessonNum?: number;
}

/** Data for a self-check item extracted from the roadmap. */
export interface RoadmapSelfCheckData extends RoadmapItemData {
  selfCheckId: string;
}
